import { FC, useEffect, useMemo, useState } from 'react';
import { connect, type ConnectedProps, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import { CurrencySymbolEnum } from '../../lib/enums/currency.enum';
import { GapperParamType } from '../../lib/models/fundamental/types';
import { RootState } from '../../lib/store';
import { getGappers } from '../../lib/store/fundamental';
import { DEFAULT_PAGE_LIST_SIZE } from '../../lib/store/fundamental/constants';
import { FundamentalCall } from '../../lib/store/fundamental/types';
import { isCallStatusPending } from '../../lib/util/error-handling/StatusByCallHelpers';

import { usePriceAndChartLoading, useSubPageTitle } from '../../hooks';
import { useSubscriptionManager } from '../../hooks/useSubscriptionManager';
import HotGappersInternal from '../../shared_components/HotGappers/HotGappersInternal/HotGappersInternal';
import { PopularGroupItemRow } from '../../shared_components/PopularGroups/PopularGroups.types';
import { UI_ROUTES } from '../../util/routes';

const GAP_TRACKER_TAB_STATE_KEY = 'gapTrackerTab';
const GAP_UP_TAB_INDEX = 0;
const GAP_DOWN_TAB_INDEX = 1;

const HotGappersInternalWrapper: FC<ReduxProps> = (props: ReduxProps): JSX.Element => {
  const {
    companyLogoAndName,
    isGettingGappers,
    gappersContent,
    gappersTotalPages,
    gappersTotalElements,
  } = props;

  const dispatch = useDispatch();
  const location = useLocation();
  const titleRef = useSubPageTitle<HTMLDivElement>();
  const navigate = useNavigate();
  const locationState = (location.state as {
    abovePreviousClose?: boolean,
    [GAP_TRACKER_TAB_STATE_KEY]?: number,
  } | null) ?? {};

  const initialTabIndex = locationState[GAP_TRACKER_TAB_STATE_KEY]
    ?? (locationState.abovePreviousClose === false ? GAP_DOWN_TAB_INDEX : GAP_UP_TAB_INDEX);
  const [abovePreviousClose, setAbovePreviousClose] = useState<boolean>(initialTabIndex === GAP_UP_TAB_INDEX);
  const [gappersPage, setGappersPage] = useState<number>(0);
  const [showLoadingSpinner, setShowLoadingSpinner] = useState<boolean>(false);

  // Single market: the price cache is keyed by the plain symbol and prices are in dollars.
  const rows = useMemo((): PopularGroupItemRow[] => gappersContent.map(item => ({
    item,
    chartSymbol: item.symbol,
    countryDesignation: '',
    currencySymbol: CurrencySymbolEnum.USD,
    isEuropeanSymbol: false,
    companyName: item.name ?? companyLogoAndName[item.symbol]?.name ?? '',
  })), [gappersContent, companyLogoAndName]);

  const symbolsToSubscribe = useMemo(() => gappersContent.map(item => ({
    symbol: item.symbol,
    stockExchangeCode: item.stockExchangeCode,
    countryDesignation: companyLogoAndName[item.symbol]?.countryDesignation,
  })), [gappersContent, companyLogoAndName]);

  useSubscriptionManager({
    symbols: symbolsToSubscribe,
    enabled: symbolsToSubscribe.length > 0 && !isGettingGappers,
  });

  const isLoading = usePriceAndChartLoading({
    stocks: gappersContent,
    isApiPending: isGettingGappers && !showLoadingSpinner,
  });

  useEffect(() => {
    const payload: GapperParamType = {
      abovePreviousClose,
      page: gappersPage,
      size: DEFAULT_PAGE_LIST_SIZE,
    };
    dispatch(getGappers(payload));
  }, [dispatch, abovePreviousClose, gappersPage]);

  const handleLoadMore = () => {
    setGappersPage(gappersPage + 1);
    setShowLoadingSpinner(true);
  };

  const handleTabChange = (index: number) => {
    setGappersPage(0);
    setAbovePreviousClose(index === GAP_UP_TAB_INDEX);
    setShowLoadingSpinner(false);
  };

  const handleSymbolClick = (symbol: string) => {
    navigate(`${UI_ROUTES.symbol}/${symbol}`);
  };

  const handleGoBack = () => navigate(UI_ROUTES.discover);

  const hasMorePages = gappersPage < gappersTotalPages
    && gappersContent.length !== gappersTotalElements
    && !isGettingGappers;

  return (
    <HotGappersInternal
      rows={rows}
      activeTabIndex={abovePreviousClose ? GAP_UP_TAB_INDEX : GAP_DOWN_TAB_INDEX}
      isPending={isGettingGappers}
      isLoading={isLoading}
      hasMorePages={hasMorePages}
      showLoadingSpinner={showLoadingSpinner}
      onTabChange={handleTabChange}
      onLoadMore={handleLoadMore}
      onSymbolClick={handleSymbolClick}
      titleRef={titleRef}
      onGoBack={handleGoBack}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  companyLogoAndName: state.fundamental.companyLogoAndName,
  isGettingGappers: isCallStatusPending(FundamentalCall.getGappers, state.fundamental.statusByCall),
  gappersContent: state.fundamental.gappersData.content,
  gappersTotalPages: state.fundamental.gappersData.totalPages,
  gappersTotalElements: state.fundamental.gappersData.totalElements,
});

const connector = connect(mapStateToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(HotGappersInternalWrapper);
